import {CircularProgress, IconButton, Typography} from '@mui/material'
import {makeStyles} from '@mui/styles'
import {useCallback, useContext, useMemo} from 'react'
import {useTranslate} from 'react-admin'

import useDownloadFileAttachments from '../../hooks/useDownloadFileAttachments'
import {ChatMessageFileAttachments} from '../../types/graphqlSchema'
import {CloseIcon, DownloadIcon, ShareIcon} from '../icons'
import {ChatContext} from './ChatContext'
import ForwardChatMessagesDialog from './ForwardChatMessagesDialog'

const ChatBulkActionsToolbar = () => {
  const styles = useStyles()
  const translate = useTranslate()
  const {
    bulkAction,
    isForwardChatMessagesDialogOpen,
    onToggleIsForwardingChatMessagesDialogOpen,
    onUnsetBulkAction,
    selectedChatMessages,
  } = useContext(ChatContext)
  const {download, isDownloading} = useDownloadFileAttachments({
    onDownload: onUnsetBulkAction,
  })
  const selectedFileAttachments = useMemo(
    () => selectedChatMessages
      .values()
      .reduce<ChatMessageFileAttachments[]>(
        (a, c) => a.concat(c.chatMessageFileAttachments),
        [],
      ),
    [selectedChatMessages],
  )
  const downloadFileAttachments = useCallback(
    () => download(selectedFileAttachments),
    [download, selectedFileAttachments],
  )
  return (
    <div className={styles.root}>
      <IconButton onClick={() => onUnsetBulkAction()}>
        <CloseIcon color="inherit"/>
      </IconButton>
      <Typography variant="subtitle2">
        {translate('ra.action.bulk_actions', {
          smart_count: bulkAction === 'FORWARD' ?
            selectedChatMessages.size :
            selectedFileAttachments.length,
        })}
      </Typography>
      <div className={styles.actionButtons}>
        {bulkAction === 'FORWARD' ? (
          <IconButton
            disabled={!selectedChatMessages.size}
            onClick={onToggleIsForwardingChatMessagesDialogOpen}
          >
            <ShareIcon />
          </IconButton>
        ) : (
          <IconButton
            disabled={!selectedFileAttachments.length || isDownloading}
            onClick={downloadFileAttachments}
          >
            {isDownloading ? <CircularProgress color="secondary" /> : <DownloadIcon />}
          </IconButton>
        )}
      </div>
      {bulkAction === 'FORWARD' && (
        <ForwardChatMessagesDialog
          onClose={onToggleIsForwardingChatMessagesDialogOpen}
          open={isForwardChatMessagesDialogOpen}
        />
      )}
    </div>
  )
}

const useStyles = makeStyles(theme => ({
  actionButtons: {
    marginLeft: 'auto',
  },
  root: {
    [theme.breakpoints.down('lg')]: {
      marginBottom: `calc(var(--safe-area-bottom-inset, 1rem) - 1rem)`,
    },
    alignItems: 'center',
    background: theme.palette.background.paper,
    color: theme.palette.secondary.main,
    display: 'flex',
    gap: theme.remSpacing(3),
    padding: `${theme.remSpacing(1.65)} ${theme.remSpacing(2)}`,
  },
}))

export default ChatBulkActionsToolbar
